import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  TextField,
  Collapse,
  Tooltip,
  Chip,
  Stack,
} from '@mui/material';
import { getLabelsByInstituteType } from '../../utils/constants';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import ModalDialog from './ModalDialog';
import { useSelector } from 'react-redux';

const OrgStructure = ({
  data = [],
  onAdd,
  onRemove,
  onEdit,
  showAddButton = true,
  showRemoveButton = true,
  showEditButton = false,
  enableExpansion = true,
  maxDepth = 5,
  cardVariant = 'outlined',
  title = 'Organization Structure',
  className = '',
  itemIcon = null,
  //customActions = [],
  ...props
}) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(''); // 'add', 'edit', 'remove'
  const [selectedNode, setSelectedNode] = useState(null);
  const [level, setLevel] = useState(0);
  const [modalTitle, setModalTitle] = useState('');
  const [showError, setShowError] = useState(true);
  const [formData, setFormData] = useState({ name: '', description: '' });
  let { instituteType } = useSelector((state) => {
    return state.auth?.successData?.userDetails ?? {};
  });
  const [submitting, isSubmitting] = useState(false);
  let { Department, Class, Section } = getLabelsByInstituteType(instituteType);

  // Toggle node expansion
  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  // Open modal for different actions
  const openModal = (type, node = null, parentNode = null, num) => {
    setModalTitle(
      num === 1
        ? Department
        : num === 2
          ? Class
          : num === 3
            ? Section
            : 'Member',
    );
    setModalType(type);
    setSelectedNode({ ...node, parentNode });
    setFormData({
      name: node ? node.name : '',
      description: node ? node.description : '',
    });
    setModalOpen(true);
  };

  // Close modal and reset state
  const closeModal = () => {
    setShowError(true);
    setModalOpen(false);
    setModalType('');
    setSelectedNode(null);
    setFormData({ name: '', description: '' });
  };

  // Handle form submission
  const handleSubmit = () => {
    isSubmitting(true);
    if (!formData.name.trim()) return;

    const nodeData = {
      id: selectedNode?.id || Date.now().toString(),
      name: formData?.name.trim(),
      description: formData?.description?.trim(),
      children: selectedNode?.children || [],
    };

    switch (modalType) {
      case 'add':
        try {
          if (!submitting) {
            isSubmitting(true);
            onAdd &&
              onAdd(nodeData, selectedNode?.parentNode, level, closeModal).then(
                (response) => setShowError(response),
              );
          }
        } catch (error) {
          console.log(error);
        } finally {
          isSubmitting(false);
        }
        break;
      case 'edit':
        onEdit &&
          onEdit({ ...selectedNode, ...nodeData }, level, closeModal).then(
            (response) => setShowError(response),
          );
        isSubmitting(false);
        break;
      case 'remove':
        onRemove &&
          onRemove(selectedNode, level, closeModal).then((response) => {
            setShowError(response);
          });
        isSubmitting(false);
        break;
      default:
        break;
    }
  };

  // Render individual node
  const renderNode = (node, depth = 0, parentNode = null) => {
    if (depth > maxDepth) {
      return;
    }
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const canAddChildren = depth < maxDepth;
    const window_width = window.innerWidth;
    let multiplier = window_width > 620 ? 4 : 2;

    return (
      <Box key={node.id} sx={{ ml: depth * multiplier }}>
        <Card
          variant={cardVariant}
          sx={{
            mb: 1,
            backgroundColor: depth === 0 ? 'primary.50' : 'background.paper',
            border: depth === 0 ? 2 : 1,
            borderColor: depth === 0 ? 'primary.main' : 'divider',
          }}
        >
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              flexWrap='wrap'
            >
              <Box display='flex' alignItems='center' flex={1}>
                {/* Expansion toggle */}
                {hasChildren && enableExpansion && depth < maxDepth && (
                  <IconButton
                    size='small'
                    onClick={() => toggleNode(node.id)}
                    sx={{ mr: 1 }}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                )}

                {/* Node icon */}
                <Box sx={{ mr: 1, color: 'primary.main' }}>
                  {depth === 0 && itemIcon}
                </Box>
                {/* Node content */}
                <Box flex={1}>
                  <div className=' w-fit flex '>
                    <div>
                      <Typography variant='subtitle2' fontWeight='bold'>
                        {node.name}
                      </Typography>
                    </div>
                    {depth === 1 ? (
                      <Chip
                        label={Department}
                        className='-mt-3 ml-3 bg-orange-100'
                      />
                    ) : depth === 2 ? (
                      <Chip
                        label={Class}
                        className='-mt-3 ml-3 bg-indigo-100'
                      />
                    ) : depth === 3 ? (
                      <Chip label={Section} className='-mt-3 ml-3 bg-red-100' />
                    ) : (
                      ''
                    )}
                  </div>

                  {node.description && (
                    <Typography variant='body2' color='text.secondary' noWrap>
                      {node.description}
                    </Typography>
                  )}
                  {hasChildren && !isExpanded && depth < maxDepth && (
                    <Chip
                      label={
                        depth === 0
                          ? `${node.children.length} ${Department}${node.children.length !== 1 ? 's' : ''}`
                          : depth === 1
                            ? `${node.children.length} ${Class}${node.children.length !== 1 ? 's' : ''}`
                            : depth === 2
                              ? `${node.children.length} ${Section}${node.children.length !== 1 ? 's' : ''}`
                              : ''
                      }
                      size='small'
                      variant='outlined'
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Action buttons */}
              {depth === 0 && showAddButton && canAddChildren && (
                <div className='flex  items-center justify-end  md:pr-[8.1rem] font-semibold ml-auto '>
                  <div> Click to add {Department}</div>
                  <Tooltip title='Add child'>
                    <IconButton
                      className='bg-blue-100 ml-2'
                      size='small'
                      onClick={() => {
                        openModal('add', null, node, depth + 1);
                        setLevel(depth);
                      }}
                      color='primary'
                    >
                      <AddIcon className='size-[24px]' />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
              {depth !== 0 && (
                <Stack
                  direction='row'
                  spacing={0.5}
                  className='md:mr-12 ml-auto'
                >
                  {/* Custom actions */}
                  {/* {customActions.map((action, index) => (
                    <Tooltip key={index} title={action.tooltip || action.label}>
                      <IconButton
                        size='small'
                        onClick={() => action.onClick(node, parentNode)}
                        disabled={action.disabled?.(node)}
                        color={action.color || 'default'}
                      >
                        {action.icon}
                      </IconButton>
                    </Tooltip>
                  ))} */}

                  {/* Add button */}
                  {showAddButton && canAddChildren && (
                    <Tooltip title='Add child'>
                      <IconButton
                        size='small'
                        onClick={() => {
                          openModal('add', null, node, depth + 1);
                          setLevel(depth);
                        }}
                        color='primary'
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Edit button */}
                  {showEditButton && (
                    <Tooltip title='Edit'>
                      <IconButton
                        size='small'
                        onClick={() => {
                          openModal('edit', node, parentNode, depth);
                          setLevel(depth);
                        }}
                        color='info'
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Remove button */}
                  {showRemoveButton && (
                    <Tooltip title='Remove'>
                      <IconButton
                        size='small'
                        onClick={() => {
                          openModal('remove', node, parentNode, depth);
                          setLevel(depth);
                        }}
                        color='error'
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Render children */}
        {hasChildren && enableExpansion && (
          <Collapse in={isExpanded}>
            <Box>
              {node.children.map((child) => renderNode(child, depth + 1, node))}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  // Modal content for different actions
  const renderModalContent = () => {
    switch (modalType) {
      case 'add':
        return (
          <Box>
            <Typography variant='body1' sx={{ mb: 2 }}>
              Add new {modalTitle} under{' '}
              {selectedNode?.parentNode
                ? selectedNode.parentNode.name
                : 'organization'}
            </Typography>
            <TextField
              fullWidth
              label='Name *'
              value={formData.name}
              onChange={(e) => {
                const val = e.target.value;
                setFormData({
                  ...formData,
                  name: val.slice(0, 1)?.toUpperCase() + val.slice(1),
                });
                setShowError(true);
              }}
              margin='normal'
              autoFocus
            />
            {/* <TextField
              fullWidth
              label='Description'
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              margin='normal'
              multiline
              rows={3}
            /> */}
            {!showError && (
              <Typography color='error'>
                {formData.name} already exists !
              </Typography>
            )}
          </Box>
        );

      case 'edit':
        return (
          <Box>
            <Typography variant='body1' sx={{ mb: 2 }}>
              Edit {selectedNode?.name}
            </Typography>
            <TextField
              fullWidth
              label='Name *'
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                setShowError(true);
              }}
              margin='normal'
              autoFocus
            />
            {/* <TextField
              fullWidth
              label='Description'
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              margin='normal'
              multiline
              rows={3}
            /> */}
            {!showError && (
              <Typography color='error'>
                {formData.name} already exists !
              </Typography>
            )}
          </Box>
        );

      case 'remove':
        return (
          <Box>
            <Typography variant='body1' sx={{ mb: 2 }}>
              Are you sure you want to remove{' '}
              <strong>{selectedNode?.name}</strong>?
            </Typography>
            {selectedNode?.children && selectedNode.children.length > 0 && (
              <>
                <Typography variant='body2' color='warning.main'>
                  Warning: This will also remove{' '}
                  {selectedNode?.children?.length} child member(s).
                </Typography>
                <br />
              </>
            )}

            {!showError && (
              <Typography color='error'>
                Students are mapped under {selectedNode?.name},so it cannot be
                Deleted .{' '}
              </Typography>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  // Modal footer buttons
  const renderModalFooter = () => (
    <Box>
      <div></div>
      <Button onClick={closeModal} sx={{ mr: 1 }}>
        Cancel
      </Button>
      <Button
        variant='contained'
        onClick={handleSubmit}
        color={modalType === 'remove' ? 'error' : 'primary'}
        disabled={!formData.name.trim() || submitting}
      >
        {modalType === 'add'
          ? 'Add'
          : modalType === 'edit'
            ? 'Update'
            : 'Remove'}
      </Button>
    </Box>
  );

  return (
    <Box className={className} {...props}>
      {/* Header */}
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={2}
      >
        <Typography variant='h6' component='h2'>
          {title}
        </Typography>
        {showAddButton && (
          <Button
            className='hidden'
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => openModal('add')}
            size='small'
          >
            Add Root
          </Button>
        )}
      </Box>

      {/* Organization structure */}
      <Box>
        {data.length === 0 ? (
          <Card variant='outlined'>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <BusinessIcon
                sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant='h6' color='text.secondary' gutterBottom>
                No Academic Structure
              </Typography>
            </CardContent>
          </Card>
        ) : (
          data.map((node) => renderNode(node, 0))
        )}
      </Box>

      {/* Modal */}
      <ModalDialog
        isOpen={modalOpen}
        onClose={closeModal}
        title={
          modalType === 'add'
            ? `Add New ${modalTitle}`
            : modalType === 'edit'
              ? `Edit ${modalTitle}`
              : modalType === 'remove'
                ? `Remove ${modalTitle}`
                : ''
        }
        size='sm'
        footer={renderModalFooter()}
      >
        {renderModalContent()}
      </ModalDialog>
    </Box>
  );
};

export default OrgStructure;
